import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Chip,
  Stack,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Building2, UserPlus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useTranslation } from 'react-i18next';

const CreateUser = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const { user, register } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const organizationId = searchParams.get('organization');
  const from = location.state?.from || { pathname: '/profiili' };
  console.log('from, location.state?.from',from, location.state?.from)

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [organizationInfo, setOrganizationInfo] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(false);

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user]);


  useEffect(() => {
    const fetchOrganizationInfo = async () => {
      if (!organizationId) return;
      setLoadingOrg(true);
      try {
        const infoDoc = await getDoc(doc(db, 'organizations', organizationId, 'public', 'info'));
        if (infoDoc.exists()) {
          setOrganizationInfo({ id: infoDoc.id, ...infoDoc.data() });
        } else {
          setError(t('auth.error.organizationInfoNotFound'));
        }
      } catch {
        setError(t('auth.error.organizationInfoFetchFailed'));
      } finally {
        setLoadingOrg(false);
      }
    };
    fetchOrganizationInfo();
  }, [organizationId]);

  const validateForm = () => {
    if (!email.trim()) { setError(t('auth.error.emailRequired')); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError(t('auth.error.invalidEmail')); return false; }
    if (password.length < 6) { setError(t('auth.error.passwordTooShort')); return false; }
    if (password !== confirmPassword) { setError(t('auth.error.passwordMismatch')); return false; }
    if (!acceptedTerms) { setError(t('auth.error.acceptTerms')); return false; }
    return true;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;
    setLoading(true);
    try {
      const userData = {
        email: email.trim(),
      };
      if (organizationId) {
        userData.organizationId = organizationId;
        userData.organizationName = organizationInfo?.name || '';
      }
      const result = await register(email, password, userData);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || t('auth.accountCreationFailed'));
      }
    } catch (err) {
      setError(err.message || t('auth.error.unknownError'));
    } finally {
      setLoading(false);
    }
  };
  if (loadingOrg) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">{t('auth.loadingOrganizationInfo')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        pt: 4,
        pb: 6,
      }}
    >
      <Container maxWidth="sm">
        {/* Brand header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 52,
              height: 52,
              borderRadius: '14px',
              bgcolor: 'primary.main',
              boxShadow: isDark
                ? '0 0 0 8px rgba(34,211,238,0.08)'
                : '0 0 0 8px rgba(8,145,178,0.08)',
              mb: 2.5,
            }}
          >
            <UserPlus size={26} color="#fff" />
          </Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            {t('auth.createAccount')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('auth.createAccountDesc')}
          </Typography>
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            {organizationInfo && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: 'primary.50',
                  borderColor: 'primary.main',
                  borderRadius: '10px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Building2 size={16} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t('auth.organizationInvite')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('auth.inviteMessage')}
                </Typography>
                <Chip
                  label={organizationInfo.name}
                  color="primary"
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Paper>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label={t('auth.email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />

                <TextField
                  fullWidth
                  label={t('auth.password')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  helperText={t('auth.passwordHelper')}
                />

                <TextField
                  fullWidth
                  label={t('auth.confirmPassword')}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      {t('auth.acceptTerms')}{' '}
                      <Link href="/terms" target="_blank" rel="noopener" underline="hover" color="primary">
                        {t('auth.termsOfService')}
                      </Link>
                    </Typography>
                  }
                />
              </Stack>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ mt: 3 }}
              >
                {loading ? t('auth.creatingAccount') : t('auth.createAccount')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('auth.alreadyHaveAccount')}
          </Typography>
          <Button variant="text" color="primary" onClick={() => navigate('/auth')}>
            {t('auth.login')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CreateUser;